import React from 'react';
import { CrossIcon, SearchIcon } from '@mergermarket/acuris-react-icon';
import { breakpoints, colors, warmGrey, divisionColors } from '@mergermarket/acuris-react-styles';
import styled from 'styled-components';

const StyledSearchActions = styled.div`
  display: inline-block;
  text-align: right;
  width: 9rem;
  margin: 0;
`;
const StyledSearchButtonClear = styled.button`
  display: inline-block;
  border-radius: 0.3rem;
  margin: 0;
  padding: 0;
  width: 3.2rem;
  height: 3.2rem;
  padding: 0;
  background: transparent;
  border: 0;
  position: absolute;
  right: ${props => props.searchType === "exact-match" ? "0" : "3.3rem"};
  top: 0;

  &:focus {
    outline: none;

    @media screen and (min-width: ${breakpoints.small}) {
      outline: solid 0.1rem ${colors.outline};
    }

    & .icon {
      fill: ${warmGrey.VII};
    }
  }

  @media screen and (min-width: 66.625em) {
    height: 3.6rem;
  }

  & .icon--cross {
    fill: ${warmGrey.VI};
    width: 1.2rem;
    height: 1.2rem;
    position: relative;
    top: -0.2rem;
    left: 0;
  }
`;
const StyledSearchButtonSubmit = styled(StyledSearchButtonClear)`
  border-radius: 0 0.2rem 0.2rem 0;
  background: ${warmGrey.II};
  display: inline-block;

  ${props => props.color ? "height:3.2rem;top:0;right:0;" : "height:3rem;top:0.1rem;right:0.1rem;"};

  @media screen and (min-width: 66.625em) {
    height: ${props => props.color ? "3.6rem" : "3.5rem"};
  }

  &:focus {
    & .icon {
      fill: ${divisionColors.darker};
    }
  }

  &:hover {
    & .icon {
      fill: ${props => props.disabled ? warmGrey.VI : divisionColors.dark};
    }
  }

  &:active {
    box-shadow: ${props => props.disabled ? `inset 0 0 0 ${warmGrey.VII};` : `inset 0 0.3rem 0.2rem ${warmGrey.VII}`};
    & .icon {
      fill: ${props => props.disabled ? warmGrey.VI : divisionColors.dark};
    }
  }

  & .icon {
    position: static;
    margin: 0;
    width: 1.6rem;
    height: 1.6rem;
    fill: ${props => props.isActive ? divisionColors.dark : warmGrey.VI};
    position: relative;
    top: -0.2rem;
    left: 0;
  }
`;
const StyledSearchIcon = styled.span`
  width: 1.6rem;
  height: 1.6rem;
  position: absolute;
  left: 0.8rem;
  top: 1rem;

  & .icon {
    position: relative;
    margin: 0;
    width: 1.6rem;
    height: 1.6rem;
    fill: ${warmGrey.VII};
    top: -0.6rem;
    left: 0;
  }
`;

const areButtonsEnabled = (searchValue, disabled = false) => disabled || searchValue === undefined ? false : searchValue.trim().length >= 2;

const isSubmitDisabled = (searchValue, disabled = false) => disabled || searchValue === undefined ? true : searchValue.trim().length <= 1;

const SearchCTA = ({
  children,
  searchType,
  searchValue,
  color,
  disabled,
  onBlur,
  onFocus,
  onSearchClick
}) => searchType === 'exact-match' ? /*#__PURE__*/React.createElement(StyledSearchIcon, null, children) : /*#__PURE__*/React.createElement(StyledSearchButtonSubmit, {
  isActive: areButtonsEnabled(searchValue),
  onClick: onSearchClick,
  type: "submit",
  value: "search",
  onFocus: onFocus,
  onBlur: onBlur,
  disabled: isSubmitDisabled(searchValue, disabled),
  "data-test-button-submit": true,
  color: color
}, children);

const SearchActions = ({
  clearInput,
  searchType,
  searchValue,
  color,
  onBlur,
  onFocus,
  onSearchClick
}) => /*#__PURE__*/React.createElement(StyledSearchActions, null, areButtonsEnabled(searchValue) && /*#__PURE__*/React.createElement(StyledSearchButtonClear, {
  onFocus: onFocus,
  onBlur: onBlur,
  "data-test-button-clear": true,
  type: "button",
  onClick: e => {
    clearInput && clearInput(e);
    onBlur && onBlur(e);
  },
  searchType: searchType,
  "aria-label": `Clear ${searchValue} from your search`
}, /*#__PURE__*/React.createElement(CrossIcon, {
  className: "icon--medium icon--cross"
})), /*#__PURE__*/React.createElement(SearchCTA, {
  searchValue: searchValue,
  onSearchClick: onSearchClick,
  searchType: searchType,
  onFocus: onFocus,
  onBlur: onBlur,
  color: color
}, /*#__PURE__*/React.createElement(SearchIcon, {
  title: "search"
})));

const StyledSearchInput = styled.input`
  font-size: 1.6rem;
  font-weight: 400;
  border: 0;
  border-radius: 0.3rem 0 0 0.3rem;
  display: inline-block;
  margin: -0.1rem 0 0 0;
  padding: 0;
  padding-left: ${props => props.searchType === 'exact-match' ? '2.8rem' : '0.8rem'};
  width: calc(100% - 9rem);
  height: 3.1rem;

  &::-ms-clear {
    display: none;
  }

  &:focus {
    outline-width: 0;
    outline-style: none;
  }
  &::placeholder {
    color: ${warmGrey.VII};
  }

  @media screen and (min-width: 66.625em) {
    height: 3.5rem;
  }
`;

// to expose the focus on the input DOM element forwardRef is needed

const onInputKeyPress = (e, onEnter, searchType) => {
  if (e.key === 'Enter' && searchType && searchType === 'exact-match') {
    e.preventDefault();
  } else if (e.key === 'Enter' && onEnter) {
    e.preventDefault();
    onEnter(e);
  }
};

const SearchInput = React.forwardRef(({
  autoComplete,
  disabled,
  inputFieldId,
  label,
  placeholder,
  searchType,
  searchValue,
  ariaDescribedby,
  ariaControls,
  onBlur,
  onChange,
  onEnter,
  onFocus,
  onKeyDown
}, ref) => /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("label", {
  className: "visuallyhidden",
  htmlFor: inputFieldId
}, label), /*#__PURE__*/React.createElement(StyledSearchInput, {
  id: inputFieldId,
  autoComplete: autoComplete,
  disabled: disabled,
  placeholder: placeholder,
  ref: ref,
  searchType: searchType,
  value: searchValue,
  "aria-describedby": ariaDescribedby,
  "aria-controls": ariaControls,
  onBlur: onBlur,
  onChange: onChange,
  onFocus: onFocus,
  onKeyPress: e => onInputKeyPress(e, onEnter, searchType),
  onKeyDown: e => onKeyDown && onKeyDown(e)
})));

const StyledWrapper = styled.div`
  border-bottom: 0;
  height: auto;
  position: relative;
  max-width: 70rem;
`;
const StyledForm = styled.form`
  overflow: hidden;
  padding: 0;
  background: ${colors.primaryBackground};
  border-radius: 0.3rem;
  border: solid 0.1rem ${props => props.color ? "transparent" : warmGrey.III};
  ${props => props.isFocused && `box-shadow: 0 0.1rem 0.2rem 0 ${warmGrey.VII};`};
`;

class Search extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      isFocused: false
    };
    this.searchRef = React.createRef();
    this.formRef = React.createRef();
    this.onBlur = this.onBlur.bind(this);
    this.onFocus = this.onFocus.bind(this);
    this.onEnter = this.onEnter.bind(this);
    this.onSearchBoxClicked = this.onSearchBoxClicked.bind(this);
  }

  render() {
    const {
      id,
      label,
      placeholder,
      searchType,
      autoComplete,
      action,
      className,
      color,
      ariaDescribedby,
      ariaControls,
      disabled = false
    } = this.props;
    return /*#__PURE__*/React.createElement(StyledWrapper, {
      className: `search${className ? ` ${className}` : ''}`
    }, /*#__PURE__*/React.createElement(StyledForm, {
      ref: this.formRef,
      isFocused: this.state.isFocused,
      action: action,
      color: color
    }, /*#__PURE__*/React.createElement("div", {
      role: "search",
      onClick: this.onSearchBoxClicked,
      id: id
    }, /*#__PURE__*/React.createElement(SearchInput, {
      inputFieldId: `${id}_field`,
      label: label,
      onFocus: this.onFocus,
      onBlur: this.onBlur,
      placeholder: placeholder,
      ref: this.searchRef,
      searchType: searchType,
      ariaDescribedby: ariaDescribedby,
      ariaControls: ariaControls,
      onChange: this.props.onChange,
      onEnter: this.onEnter,
      searchValue: this.props.searchValue,
      autoComplete: autoComplete,
      onKeyDown: this.props.onKeyDown
    }), /*#__PURE__*/React.createElement(SearchActions, {
      onSearchClick: this.onEnter,
      onFocus: this.onFocus,
      onBlur: this.onBlur,
      searchType: searchType,
      disabled: disabled,
      clearInput: this.props.onClear,
      searchValue: this.props.searchValue,
      color: color
    }))));
  }

  onFocus(e) {
    this.setState({
      isFocused: true
    });

    if (typeof this.props.onFocus === 'function') {
      this.props.onFocus(e);
    }
  }

  onBlur(e) {
    this.setState({
      isFocused: false
    });

    if (typeof this.props.onBlur === 'function') {
      this.props.onBlur(e);
    }
  }

  onEnter() {
    if (this.props.onSubmit) {
      const inputEl = this.formRef.current.querySelector(`#${this.props.id}`);
      this.props.onSubmit(inputEl.value);
    } else {
      this.formRef.current.submit();
    }
  }

  onSearchBoxClicked(e) {
    e.preventDefault();

    if (this.searchRef && this.searchRef.current) {
      this.searchRef.current.focus();
    }
  }

}

export default Search;
export { SearchActions, SearchInput };
//# sourceMappingURL=index.js.map
